import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import {
  FaChartBar as FaChartBarRaw,
  FaCopy as FaCopyRaw,
  FaEdit as FaEditRaw,
  FaEnvelope as FaEnvelopeRaw,
  FaSearch as FaSearchRaw,
  FaSignal as FaSignalRaw,
  FaSms as FaSmsRaw,
  FaTrashAlt as FaTrashAltRaw,
  FaUsers as FaUsersRaw,
} from 'react-icons/fa';
import { useMarketingCampaigns, useMarketingSubscribers } from '../hooks/useMarketing';
import MarketingHeader from '../components/MarketingHeader';
import {
  MarketingPageShell,
  PrimaryButtonLink as SharedPrimaryButtonLink,
} from '../styles/marketingSharedStyles';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaSearch: any = FaSearchRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaSms: any = FaSmsRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaEnvelope: any = FaEnvelopeRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaSignal: any = FaSignalRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaChartBar: any = FaChartBarRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaEdit: any = FaEditRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaCopy: any = FaCopyRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaTrashAlt: any = FaTrashAltRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaUsers: any = FaUsersRaw;

type CampaignStatusView = 'active' | 'scheduled' | 'completed' | 'draft';
type ChannelFilter = 'all' | 'sms' | 'email';
type StatusFilter = 'all' | CampaignStatusView;

const Page = styled(MarketingPageShell)`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  padding-bottom: 1rem;
  overflow-x: clip;
`;

const CampaignSection = styled.section`
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.035) 0%, rgba(255, 255, 255, 0.01) 100%), var(--surface);
  border: 1px solid var(--outline);
  border-radius: 16px;
  padding: 1.75rem 2rem;
  box-shadow: 0 16px 34px rgba(0, 0, 0, 0.16);
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const TopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.25rem;
  margin-bottom: 1.75rem;
  flex-wrap: wrap;
`;

const HeadingWrap = styled.div``;

const Title = styled.h1`
  margin: 0;
  font-size: clamp(1.35rem, 2.4vw, 1.85rem);
  color: var(--onSurface);
  letter-spacing: -0.02em;
`;

const Subtitle = styled.p`
  margin: 0.5rem 0 0 0;
  color: var(--onSurfaceVariant);
  font-size: 0.95rem;
  line-height: 1.4;
`;

const ControlsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.25rem;
  flex-wrap: wrap;
  margin-bottom: 1.5rem;
`;

const SearchWrap = styled.label`
  display: flex;
  align-items: center;
  gap: 0.65rem;
  flex: 1;
  min-width: 260px;
  border: 1px solid var(--outline);
  border-radius: 10px;
  padding: 0.7rem 1rem;
  background: var(--surface);
  color: var(--onSurfaceVariant);
  @media (max-width: 768px) {
    min-width: 0;
    width: 100%;
  }
`;

const SearchInput = styled.input`
  flex: 1;
  min-width: 140px;
  border: none;
  outline: none;
  background: transparent;
  color: var(--onSurface);
  font-size: 0.94rem;
`;

const Chips = styled.div`
  display: flex;
  align-items: center;
  gap: 0.65rem;
  flex-wrap: wrap;
`;

const Chip = styled.button<{ $active?: boolean }>`
  border: 1px solid ${({ $active }) => ($active ? 'var(--primary)' : 'var(--outline)')};
  background: ${({ $active }) => ($active ? 'var(--primary)' : 'var(--surface)')};
  color: ${({ $active }) => ($active ? 'var(--onPrimary)' : 'var(--onSurfaceVariant)')};
  border-radius: 10px;
  padding: 0.5rem 1rem;
  font-size: 0.84rem;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  transition: background 0.2s ease, color 0.2s ease, border-color 0.2s ease;
`;

const TableWrap = styled.div`
  border: 1px solid var(--outline);
  border-radius: 16px;
  overflow-x: auto;
  margin-bottom: 0.5rem;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;

  th {
    text-align: left;
    font-size: 0.76rem;
    color: var(--onSurfaceVariant);
    letter-spacing: 0.03em;
    text-transform: uppercase;
    padding: 1rem 1.25rem;
    border-bottom: 1px solid var(--outline);
    white-space: nowrap;
  }

  td {
    padding: 1.25rem 1.25rem;
    border-bottom: 1px solid var(--outlineVariant);
    color: var(--onSurface);
    vertical-align: middle;
  }

  tbody tr:last-child td {
    border-bottom: none;
  }

  @media (max-width: 860px) {
    thead {
      display: none;
    }
    tbody tr {
      display: block;
      border-bottom: 1px solid var(--outline);
      padding: 0.35rem 0;
    }
    tbody tr:last-child {
      border-bottom: none;
    }
    td {
      display: block;
      border-bottom: none;
      padding: 0.45rem 0.9rem;
    }
    td::before {
      content: attr(data-label);
      display: inline-block;
      min-width: 7.6rem;
      margin-right: 0.5rem;
      color: var(--onSurfaceVariant);
      font-size: 0.76rem;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.02em;
    }
  }
`;

const CampaignLink = styled(Link)`
  color: var(--onSurface);
  text-decoration: none;
  font-weight: 700;

  &:hover {
    color: var(--primary);
  }
`;

const RowMuted = styled.div`
  color: var(--onSurfaceVariant);
  font-size: 0.82rem;
  margin-top: 0.35rem;
  line-height: 1.35;
`;

const Inline = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
`;

const StatusBadge = styled.span<{ $status: CampaignStatusView }>`
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 0.35rem 0.75rem;
  font-size: 0.75rem;
  font-weight: 700;
  background: ${({ $status }) =>
    $status === 'completed'
      ? 'var(--successContainer)'
      : $status === 'scheduled'
        ? 'var(--warningContainer)'
        : $status === 'active'
          ? 'var(--primaryContainer)'
          : 'var(--surfaceVariant)'};
  color: ${({ $status }) =>
    $status === 'completed'
      ? 'var(--success)'
      : $status === 'scheduled'
        ? 'var(--warning)'
        : $status === 'active'
          ? 'var(--primary)'
          : 'var(--onSurfaceVariant)'};
`;

const PerformanceWrap = styled.div`
  min-width: 120px;
`;

const PerfText = styled.div`
  font-size: 0.84rem;
  color: var(--onSurfaceVariant);
  margin-bottom: 0.5rem;
  line-height: 1.3;
`;

const PerfBar = styled.div`
  height: 8px;
  border-radius: 999px;
  background: var(--outlineVariant);
  overflow: hidden;
`;

const PerfFill = styled.div<{ $width: number }>`
  width: ${({ $width }) => Math.max(0, Math.min($width, 100))}%;
  height: 100%;
  background: linear-gradient(90deg, #6e56f7, #75d6c7);
`;

const ActionRow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
`;

const ActionButton = styled.button`
  width: 34px;
  height: 34px;
  border: 1px solid var(--outline);
  background: var(--surface);
  color: var(--onSurfaceVariant);
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  &:hover {
    color: var(--primary);
    border-color: var(--primary);
  }
`;

const FooterBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
  padding: 1.25rem 0.5rem 0 0.5rem;
  color: var(--onSurfaceVariant);
  font-size: 0.86rem;
  line-height: 1.4;
`;

const Pager = styled.div`
  display: inline-flex;
  gap: 0.6rem;
`;

const PagerButton = styled.button`
  border: 1px solid var(--outline);
  background: var(--surface);
  border-radius: 8px;
  color: var(--onSurfaceVariant);
  font-size: 0.84rem;
  padding: 0.5rem 0.85rem;
  cursor: pointer;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1.5rem;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const StatCard = styled.div`
  border: 1px solid var(--outline);
  border-radius: 16px;
  padding: 1.5rem 1.25rem;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.035) 0%, rgba(255, 255, 255, 0.01) 100%), var(--surface);
  box-shadow: 0 16px 34px rgba(0, 0, 0, 0.14);
`;

const StatLabel = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  color: var(--onSurfaceVariant);
  font-size: 0.88rem;
  margin-bottom: 0.5rem;
`;

const StatValue = styled.div`
  color: var(--primary);
  font-size: 2rem;
  font-weight: 700;
  line-height: 1;
  margin-bottom: 0.4rem;
`;

const StatSub = styled.div`
  color: var(--onSurfaceVariant);
  font-size: 0.84rem;
  line-height: 1.4;
`;

const Empty = styled.div`
  padding: 1.5rem 1.25rem;
  margin-top: 0.5rem;
  color: var(--onSurfaceVariant);
  border: 1px dashed var(--outline);
  border-radius: 10px;
  line-height: 1.4;
`;

const ErrorText = styled.div`
  color: var(--error);
  font-size: 0.88rem;
  margin-top: 0.5rem;
  padding: 1rem 0;
`;

const statusLabel: Record<CampaignStatusView, string> = {
  active: 'Sending',
  scheduled: 'Scheduled',
  completed: 'Completed',
  draft: 'Draft',
};

const statusFilterOptions: Array<{ value: StatusFilter; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'completed', label: 'Completed' },
];

function formatRelativeDate(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffHours = Math.floor(diffMs / 3_600_000);
  const diffDays = Math.floor(diffMs / 86_400_000);
  if (diffHours < 24) return `Last updated ${Math.max(diffHours, 1)}h ago`;
  if (diffDays < 7) return `Last updated ${diffDays} days ago`;
  return `Updated ${date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}`;
}

function extractCampaignStats(campaignMetadata: Record<string, unknown>) {
  const rawStats =
    typeof campaignMetadata.twilio_stats === 'object' && campaignMetadata.twilio_stats
      ? (campaignMetadata.twilio_stats as Record<string, unknown>)
      : {};
  const sent = Number(rawStats.sent_total ?? 0);
  const failed = Number(rawStats.failed_total ?? 0);
  const total = sent + failed;
  const deliveryRate = total > 0 ? Math.round((sent / total) * 100) : 0;
  return { sent, failed, total, deliveryRate };
}

function deriveCampaignStatus(status: string | null, sent: number): CampaignStatusView {
  if (sent > 0) return 'completed';
  if (status === 'scheduled') return 'scheduled';
  if (status === 'active') return 'active';
  return 'draft';
}

const MarketingPage: React.FC = () => {
  const campaigns = useMarketingCampaigns();
  const subscribers = useMarketingSubscribers();
  const [search, setSearch] = useState('');
  const [channelFilter, setChannelFilter] = useState<ChannelFilter>('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [currentPage, setCurrentPage] = useState(1);

  const campaignsList = useMemo(() => campaigns.data ?? [], [campaigns.data]);

  const normalized = useMemo(
    () =>
      campaignsList.map((campaign) => {
        const metadata = (campaign.metadata ?? {}) as Record<string, unknown>;
        const stats = extractCampaignStats(metadata);
        const status = deriveCampaignStatus(campaign.status, stats.sent);
        const audience = stats.total > 0 ? stats.total : null;
        return {
          ...campaign,
          statusView: status,
          audience,
          deliveryRate: stats.deliveryRate,
        };
      }),
    [campaignsList]
  );

  const filtered = useMemo(
    () =>
      normalized.filter((campaign) => {
        if (search.trim() && !campaign.name.toLowerCase().includes(search.trim().toLowerCase())) return false;
        if (channelFilter !== 'all' && campaign.channel !== channelFilter) return false;
        if (statusFilter !== 'all' && campaign.statusView !== statusFilter) return false;
        return true;
      }),
    [normalized, search, channelFilter, statusFilter]
  );

  const pageSize = 6;
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const page = Math.min(currentPage, totalPages);
  const visibleCampaigns = filtered.slice((page - 1) * pageSize, page * pageSize);

  const statCards = useMemo(() => {
    const completed = normalized.filter((campaign) => campaign.statusView === 'completed');
    const averageCtr = completed.length > 0 ? completed.reduce((sum, campaign) => sum + campaign.deliveryRate, 0) / completed.length : 0;
    const totalReach = normalized.reduce((sum, campaign) => sum + (campaign.audience ?? 0), 0);
    const activeConversations = (subscribers.data ?? []).filter((subscriber) =>
      (subscriber.channel_memberships ?? []).some((membership) => !membership.unsubscribed_at)
    ).length;
    return {
      averageCtr,
      totalReach,
      activeConversations,
    };
  }, [normalized, subscribers.data]);

  return (
    <Page>
      <MarketingHeader
        activeTab="messages"
        rightSlot={
          <SharedPrimaryButtonLink to="/marketing/new-message">
            + Create New Message
          </SharedPrimaryButtonLink>
        }
      />

      <CampaignSection>
        <TopRow>
          <HeadingWrap>
            <Title>Messages</Title>
            <Subtitle>Manage and track your marketing messages</Subtitle>
          </HeadingWrap>
        </TopRow>

        <ControlsRow>
          <SearchWrap>
            <FaSearch size={13} />
            <SearchInput
              value={search}
              onChange={(event) => {
                setCurrentPage(1);
                setSearch(event.target.value);
              }}
              placeholder="Search messages by name..."
            />
          </SearchWrap>

          <Chips>
            <Chip $active={channelFilter === 'all'} onClick={() => setChannelFilter('all')}>
              All
            </Chip>
            <Chip $active={channelFilter === 'sms'} onClick={() => setChannelFilter('sms')}>
              <FaSms size={12} /> SMS
            </Chip>
            <Chip $active={channelFilter === 'email'} onClick={() => setChannelFilter('email')}>
              <FaEnvelope size={12} /> Email
            </Chip>
          </Chips>

          <Chips>
            {statusFilterOptions.map((option) => (
              <Chip
                key={option.value}
                $active={statusFilter === option.value}
                onClick={() => {
                  setCurrentPage(1);
                  setStatusFilter(option.value);
                }}
              >
                {option.label}
              </Chip>
            ))}
          </Chips>
        </ControlsRow>

        {campaigns.error && <ErrorText>Unable to load messages right now.</ErrorText>}
        {campaigns.isLoading && <Empty>Loading messages...</Empty>}
        {!campaigns.isLoading && !campaigns.error && filtered.length === 0 && (
          <Empty>No messages match your search and filter settings.</Empty>
        )}

        {!campaigns.isLoading && !campaigns.error && filtered.length > 0 && (
          <>
            <TableWrap>
              <Table>
                <thead>
                  <tr>
                    <th>Message Name</th>
                    <th>Type</th>
                    <th>Status</th>
                    <th>Audience</th>
                    <th>Performance</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleCampaigns.map((campaign) => (
                    <tr key={campaign.id}>
                      <td data-label="Message Name">
                        <CampaignLink to={`/marketing/messages/${campaign.id}`}>{campaign.name}</CampaignLink>
                        <RowMuted>{formatRelativeDate(campaign.updated_at)}</RowMuted>
                      </td>
                      <td data-label="Type">
                        <Inline>
                          {campaign.channel === 'sms' ? (
                            <>
                              <FaSms size={12} /> SMS
                            </>
                          ) : (
                            <>
                              <FaEnvelope size={12} /> Email
                            </>
                          )}
                        </Inline>
                      </td>
                      <td data-label="Status">
                        <StatusBadge $status={campaign.statusView}>{statusLabel[campaign.statusView]}</StatusBadge>
                      </td>
                      <td data-label="Audience">{campaign.audience ? campaign.audience.toLocaleString() : 'Pending'}</td>
                      <td data-label="Performance">
                        <PerformanceWrap>
                          <PerfText>
                            {campaign.statusView === 'scheduled'
                              ? 'Scheduled'
                              : campaign.deliveryRate > 0
                                ? `${campaign.deliveryRate}%`
                                : 'Not started'}
                          </PerfText>
                          <PerfBar>
                            <PerfFill $width={campaign.deliveryRate} />
                          </PerfBar>
                        </PerformanceWrap>
                      </td>
                      <td data-label="Actions">
                        <ActionRow>
                          <ActionButton type="button" title="Analytics">
                            <FaChartBar size={12} />
                          </ActionButton>
                          <ActionButton type="button" title="Edit">
                            <FaEdit size={12} />
                          </ActionButton>
                          <ActionButton type="button" title="Duplicate">
                            <FaCopy size={12} />
                          </ActionButton>
                          <ActionButton type="button" title="Delete">
                            <FaTrashAlt size={12} />
                          </ActionButton>
                        </ActionRow>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </TableWrap>

            <FooterBar>
              <span>
                Showing {(page - 1) * pageSize + 1}-{Math.min(page * pageSize, filtered.length)} of {filtered.length} messages
              </span>
              <Pager>
                <PagerButton type="button" onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))} disabled={page <= 1}>
                  Previous
                </PagerButton>
                <PagerButton
                  type="button"
                  onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                  disabled={page >= totalPages}
                >
                  Next
                </PagerButton>
              </Pager>
            </FooterBar>
          </>
        )}
      </CampaignSection>

      <StatsGrid>
        <StatCard>
          <StatLabel>
            <FaSignal size={12} /> Overall CTR
          </StatLabel>
          <StatValue>{statCards.averageCtr.toFixed(1)}%</StatValue>
          <StatSub>Average across all completed messages</StatSub>
        </StatCard>
        <StatCard>
          <StatLabel>
            <FaUsers size={12} /> Total Reach
          </StatLabel>
          <StatValue>{statCards.totalReach.toLocaleString()}</StatValue>
          <StatSub>Total unique recipients reached</StatSub>
        </StatCard>
        <StatCard>
          <StatLabel>
            <FaSms size={12} /> Active Conversations
          </StatLabel>
          <StatValue>{statCards.activeConversations.toLocaleString()}</StatValue>
          <StatSub>Ongoing SMS interactions</StatSub>
        </StatCard>
      </StatsGrid>
    </Page>
  );
};

export default MarketingPage;
